declare global {
  interface Window {
    /** @deprecated Les logs sont désormais coupés par défaut. Conservé comme force-off. */
    __TRACKEEZ_SILENT?: boolean;
    /** Force les logs à l'init, avant le chargement de trackeez.js (custom code Webflow). */
    __TRACKEEZ_DEBUG?: boolean;
    trackeez?: { debug: (on?: boolean) => boolean };
  }
}

const STORAGE_KEY = 'trackeez_debug';
const URL_PARAM = 'trackeez_debug';

/** localStorage lève en navigation privée Safari ou si le stockage est bloqué. */
function readStorage(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStorage(on: boolean): void {
  try {
    if (on) window.localStorage.setItem(STORAGE_KEY, '1');
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* stockage indisponible : le flag ne survivra pas à la navigation */
  }
}

/**
 * Retire `trackeez_debug` de l'URL sans recharger la page.
 *
 * Indispensable : `landing_page` (touch.ts), `firstpage_view` (page-views.ts) et les
 * events 404 captent `window.location.href` en entier. Sans ce nettoyage, le param
 * atterrirait dans `first_touch.landing_page` — écrit en setOnce, donc jamais corrigé.
 * Ce module s'initialise avant tous ces lecteurs, l'URL leur parvient propre.
 */
function stripUrlParam(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(URL_PARAM)) return;
    url.searchParams.delete(URL_PARAM);
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    /* history indisponible : le param restera dans l'URL, sans autre conséquence */
  }
}

/**
 * `?trackeez_debug=1` active et persiste, `=0` coupe et purge.
 * La persistance est nécessaire : le flux submit → thank you page change de page.
 */
function readUrlParam(): boolean | null {
  const raw = new URLSearchParams(window.location.search).get(URL_PARAM);
  if (raw === null) return null;
  stripUrlParam();
  return raw !== '0' && raw !== 'false';
}

function resolveInitial(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.__TRACKEEZ_SILENT === true) return false;

  const fromUrl = readUrlParam();
  if (fromUrl !== null) {
    writeStorage(fromUrl);
    return fromUrl;
  }

  if (window.__TRACKEEZ_DEBUG === true) return true;
  return readStorage() === '1';
}

let enabled = resolveInitial();

if (typeof window !== 'undefined') {
  window.trackeez = {
    debug(on = true): boolean {
      enabled = on;
      writeStorage(on);
      console.log(`[Trackeez] logs ${on ? 'activés' : 'coupés'}`);
      return enabled;
    },
  };
}

export const logger = {
  info(...args: unknown[]): void {
    if (enabled) console.log('[Trackeez]', ...args);
  },
  warn(...args: unknown[]): void {
    if (enabled) console.warn('[Trackeez]', ...args);
  },
  /** Rendu tabulaire d'un objet de clés/valeurs. console.table n'accepte pas de préfixe. */
  table(data: unknown): void {
    if (enabled) console.table(data);
  },
  error(...args: unknown[]): void {
    // Erreurs toujours loggées : rares, et seule trace exploitable d'un incident en prod.
    console.error('[Trackeez]', ...args);
  },
};
